import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import Footer from "components/Footer/Footer.js";
import LangToggle from "views/Product/LangToggle.js";
import recordId from "views/Product/recordId.js";
import { manufacturerLogoUrl, productImageUrl } from "config/images.js";
import { fetchCertTypes } from "utils/ugcCertTypes";
import translations from "./translations.js";
import styles from "./brandPageStyle.js";
import { loadBrand, sortProducts, certLine, formatDay } from "./brandData.js";

const useStyles = makeStyles(styles);

/**
 * Public brand page: the brand's register entry and its certified products.
 *
 * Replaces skinlyzer.com/brand/:authId for links from this site, so a reader
 * checking a certification never leaves the certifier's domain. Every product
 * card opens the product's record at /p/:authId; the list comes from an
 * endpoint that returns only currently certified products, so none of those
 * links can land on /invalid-cert.
 */
export default function BrandPage(props) {
  const classes = useStyles();
  const { match, history, ...rest } = props;
  const authId = match.params.authId;

  const [lang, setLang] = useState("hu");
  const [state, setState] = useState({ status: "loading" });
  const [certTypes, setCertTypes] = useState([]);
  const t = translations[lang];

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    loadBrand(authId).then((result) => {
      if (!cancelled) setState(result);
    });
    return () => {
      cancelled = true;
    };
  }, [authId]);

  // Never affects the page state: a failed lookup only hides the cert line.
  useEffect(() => {
    let cancelled = false;
    fetchCertTypes().then((types) => {
      if (!cancelled) setCertTypes(types);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const products = useMemo(
    () => sortProducts(state.products, t.dateLocale),
    [state.products, t.dateLocale]
  );

  const toggleLang = () => setLang((prev) => (prev === "hu" ? "en" : "hu"));
  const brand = state.brand;
  const cert = brand ? certLine(brand, certTypes, t) : null;
  const logo = brand ? manufacturerLogoUrl(brand.logoUrl) : null;
  const langRow = (
    <div className={classes.langRow}>
      <LangToggle t={t} onToggle={toggleLang} />
    </div>
  );

  return (
    <div className={classes.page}>
      <Header
        color="white"
        brand="UltraGreen"
        rightLinks={<HeaderLinks />}
        fixed
        {...rest}
      />

      {state.status !== "ready" && (
        <div className={`${classes.band} ${classes.bandGrow}`}>
          <div className={classes.inner}>
            {langRow}
            <div className={classes.stateBox}>
              {state.status === "loading" && t.loading}
              {state.status === "notFound" && (
                <React.Fragment>
                  <h1 className={classes.stateTitle}>{t.notFoundTitle}</h1>
                  <p style={{ margin: 0 }}>{t.notFoundBody}</p>
                  <Link to="/claim-check" className={classes.stateAction}>
                    {t.searchAction}
                  </Link>
                </React.Fragment>
              )}
              {state.status === "error" && (
                <React.Fragment>
                  <h1 className={classes.stateTitle}>{t.errorTitle}</h1>
                  <p style={{ margin: 0 }}>
                    {t.errorBody}{" "}
                    <a href={"mailto:" + t.contactEmail}>{t.contactEmail}</a>
                  </p>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      )}

      {state.status === "ready" && brand && (
        <React.Fragment>
          <div className={classes.band}>
            <div className={classes.inner}>
              {langRow}
              <div className={classes.masthead}>
                {logo && (
                  <div className={classes.logoBox}>
                    <img className={classes.logoImg} src={logo} alt={brand.name} />
                  </div>
                )}
                <div className={classes.mastheadText}>
                  <div className={classes.eyebrow}>
                    <span className={classes.eyebrowRule} />
                    <span className={classes.eyebrowText}>{t.eyebrow}</span>
                  </div>
                  <h1 className={classes.brandName}>{brand.name}</h1>
                  {brand.description && (
                    <p className={classes.description}>{brand.description}</p>
                  )}
                  {(cert || brand.url) && (
                    <div className={classes.facts}>
                      {cert && (
                        <div className={classes.fact}>
                          <span className={classes.factLabel}>{t.certLabel}</span>
                          <p className={classes.factValue}>{cert.label}</p>
                          {cert.date && (
                            <p className={classes.factValueMuted}>{cert.date}</p>
                          )}
                        </div>
                      )}
                      {brand.url && (
                        <div className={classes.fact}>
                          <span className={classes.factLabel}>{t.website}</span>
                          <p className={classes.factValue}>
                            <a
                              className={classes.factLink}
                              href={brand.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {displayHost(brand.url)}
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={classes.sheet}>
            <div className={classes.band}>
              <div className={classes.inner}>
                <div className={classes.productsHead}>
                  <h2 className={classes.productsTitle}>{t.productsTitle}</h2>
                  {Array.isArray(products) && products.length > 0 && (
                    <span className={classes.productsCount}>
                      {t.productsCount(products.length)}
                    </span>
                  )}
                </div>
                {products === null && (
                  <p className={classes.empty}>
                    {t.errorBody}{" "}
                    <a href={"mailto:" + t.contactEmail}>{t.contactEmail}</a>
                  </p>
                )}
                {Array.isArray(products) && products.length === 0 && (
                  <p className={classes.empty}>{t.productsEmpty}</p>
                )}
                {Array.isArray(products) && products.length > 0 && (
                  <div className={classes.grid}>
                    {products.map((p) => (
                      <Link
                        key={p.authId}
                        to={"/p/" + encodeURIComponent(p.authId)}
                        className={classes.card}
                      >
                        <div className={classes.cardPhoto}>
                          <img
                            className={classes.cardPhotoImg}
                            src={productImageUrl(p.imageName)}
                            alt=""
                            loading="lazy"
                          />
                        </div>
                        <div className={classes.cardText}>
                          <p className={classes.cardName}>{p.name}</p>
                          <p className={classes.cardMeta}>{recordId(p)}</p>
                          {p.certValidUntil && (
                            <p className={classes.cardMeta}>
                              {t.productValid}{" "}
                              {formatDay(p.certValidUntil, t.dateLocale)}
                              {t.productValidSuffix}
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </React.Fragment>
      )}

      <div className={classes.band}>
        <div className={classes.inner}>
          <p className={classes.companyLine}>{t.footerCompany}</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

/** "https://www.biobubi.hu/" -> "biobubi.hu"; the raw string when unparsable. */
function displayHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return url;
  }
}
